import Enhancer from '../enhancer';
import EnhancerApp from '../enhancer-app';
import { createElement, removeElement, setStyles } from '../utils/dom';

export default class Drawer extends Enhancer {

    animationDelay = 250;
    backdropAnimationDelay = 100;
    opener = null;
    isShown = false;
    scrollbarWidth = 0;
    bodyIsOverflowing = false;
    handleClickOutsideBind = null;
    handleEscKeyBind = null;
    handleTabKeyBind = null;

    init() {

        this.openers.forEach(opener => {
            opener.addEventListener('click', this.show.bind(this), true);
        });

        this.closers.forEach(opener => {
            opener.addEventListener('click', this.hide.bind(this), false);
        });
    }

    show(e) {

        if (this.isShown) {
            return;
        }

        this.opener = e.target;

        this.opener.setAttribute('aria-expanded', true);

        this.createBackdrop();

        this.wrapper.style.display = 'block';
        this.wrapper.setAttribute('aria-hidden', false);

        setTimeout(() => {
            this.calculateScrollbarWidth();
            this.setScrollbar();
            document.querySelector('body').classList.add('drawer-open');
            
            this.wrapper.focus();
        }, this.animationDelay);

        // Hide on click outside
        this.handleClickOutsideBind = this.handleClickOutside.bind(this);
        this.wrapper.addEventListener('click', this.handleClickOutsideBind, false);

        // Keep tab focus inside nav pane
        this.handleTabKeyBind = this.handleTabKey.bind(this);
        this.wrapper.addEventListener('keydown', this.handleTabKeyBind, true);

        // Hide when escape is pressed
        this.handleEscKeyBind = this.handleEscKey.bind(this);
        this.wrapper.addEventListener('keyup', this.handleEscKeyBind, true);

        this.isShown = true;
    }

    hide() {

        if (!this.isShown) {
            return;
        }

        document.querySelector('body').classList.remove('drawer-open');
        this.resetScrollbar();

        this.wrapper.removeEventListener('click', this.handleClickOutsideBind, false);
        this.wrapper.removeEventListener('keydown', this.handleTabKeyBind, true);
        this.wrapper.removeEventListener('keyup', this.handleEscKeyBind, true);
        this.handleClickOutsideBind = null;
        this.handleTabKeyBind = null;
        this.handleEscKeyBind = null;

        // set expanded a bit earlier to prevent screen readers from reading expanded=true
        this.opener.setAttribute('aria-expanded', false);

        setTimeout(() => {

            this.wrapper.style.display = 'none';
            this.wrapper.setAttribute('aria-hidden', true);
            this.removeBackdrop();
            
            this.opener.focus();
            this.opener = null;
            this.isShown = false;
            this.scrollbarWidth = 0;
            this.bodyIsOverflowing = false;

        }, this.animationDelay);
    }

    handleClickOutside(e) {

        if (e.target !== this.wrapper || !this.isShown) {
            return;
        }

        this.hide();
    }

    handleEscKey(e) {

        if (e.keyCode === 27 && this.isShown) {
            e.preventDefault();
            this.hide();
        }
    }

    handleTabKey(e) {

        // if tab is pressed
        if (e.keyCode === 9) {
            var focusableItemsSelector = "a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]",
                focusableItems = this.getAll(focusableItemsSelector),
                focusedItem = document.activeElement,
                numberOfFocusableItems = focusableItems.length,
                focusedItemIndex = Array.from(focusableItems).indexOf(focusedItem);

            if (e.shiftKey) {
                //back tab
                // if focused on first item and user preses back-tab, go to the last focusable item
                if (focusedItemIndex === 0) {
                    focusableItems[focusableItems.length - 1].focus();
                    e.preventDefault();
                }

            } else {
                //forward tab
                // if focused on the last item and user preses tab, go to the first focusable item
                if (focusedItemIndex === numberOfFocusableItems - 1) {
                    focusableItems[0].focus();
                    e.preventDefault();
                }
            }
        }

    }

    createBackdrop() {
        const backdrop = createElement('div', document.body);
        backdrop.classList.add('drawer-backdrop');

        setTimeout(() => {
            this.backdrop.classList.add('drawer-backdrop-show');
        }, this.backdropAnimationDelay);
    }

    removeBackdrop() {
        this.backdrop.classList.remove('drawer-backdrop-show');

        setTimeout(() => {
            removeElement(this.backdrop);
        }, this.backdropAnimationDelay);
    }

    setScrollbar() {
        if (this.bodyIsOverflowing) {
            const body = document.querySelector('body');
            body.setAttribute('data-padding-right', body.style.padding);
            body.style.paddingRight = `${this.scrollbarWidth}px`;
            this.drawerBody.style.paddingRight = `${this.scrollbarWidth}px`;
        }
    }

    resetScrollbar() {
        if (this.bodyIsOverflowing) {
            const body = document.querySelector('body');
            const originalPaddingRight = body.getAttribute('data-padding-right');
            body.style.paddingRight = originalPaddingRight || '';
            body.removeAttribute('data-padding-right');
            this.drawerBody.paddingRight = null;
        }
    }

    calculateScrollbarWidth() {
        const rect = document.body.getBoundingClientRect();
        this.bodyIsOverflowing = Math.round(rect.left + rect.right) < window.innerWidth;
        this.scrollbarWidth = this.getScrollbarWidth();
    }

    getScrollbarWidth() {
        const scrollDiv = document.createElement('div');
        setStyles(scrollDiv, {
            position: 'absolute',
            top: '-9999px',
            width: '50px',
            height: '50px',
            overflow: 'scroll'
        });
        document.body.appendChild(scrollDiv);
        const scrollbarWidth = scrollDiv.getBoundingClientRect().width - scrollDiv.clientWidth;
        document.body.removeChild(scrollDiv);
        return scrollbarWidth;
    }

    get backdrop() {
        return document.querySelector('.drawer-backdrop');
    }

    get wrapper() {
        return this.element;
    }

    get drawer() {
        return this.get('.drawer');
    }

    get drawerBody() {
        return this.get('.drawer-body');
    }

    get openers() {
        return document.querySelectorAll(`[data-toggle="drawer"][data-target="#${this.element.id}"]`);
    }

    get closers() {
        return this.getAll('[data-dismiss="drawer"]');
    }

}

EnhancerApp.register('Drawer', Drawer);